/**
 * Beat 14, *Ending* — `docs/beat-14-plan.md`. The last beat, and G4.
 *
 * Five stages. Two of them wait on her and both have a backstop, because the
 * last shot of the game must arrive whether or not the player touches a key
 * (D-198: nobody is going to play-test it, so the no-input run IS the test):
 *
 *   - `the-hill` ends when the room says the truck is gone, or at
 *     `ENDING_HILL_PATIENCE`;
 *   - `the-grass` ends on a nudge at the bell, or at `ENDING_FIND_PATIENCE`,
 *     when the room walks her to it (the backstop is the room's, on the
 *     `found` cue's `backstop` flag).
 *
 * Everything after the bell is a shot (D-095): clocks only.
 *
 * `the-find` exists only on the backstop path: it is the authored walk to the
 * bell, and it has its own stage so the mouth shot always starts with her AT
 * the bell, whichever way she got there.
 */
import type { Beat, Stage } from '@/story/types';

export const ENDING_CUES = {
  open: 'ending.open',
  /** The palette starts to drain and the truck pulls away. */
  hill: 'ending.hill',
  /** The truck is gone; the bell is in the grass. */
  grass: 'ending.grass',
  /** The backstop: the camera finds the bell and she is walked to it. */
  find: 'ending.find',
  /** Her head dips, the bell is taken up, one muffled stroke. */
  mouth: 'ending.mouth',
  /** She turns toward the farm and takes four steps. */
  turn: 'ending.turn',
  /** Cut to black ON the bell's last stroke. */
  black: 'ending.black',
} as const;

export const ENDING_FLAGS = {
  /** Raised by the room when the truck has passed out of sight. */
  truckGone: 'ending.truck-gone',
  /** Raised by the room on her nudge at the bell. */
  bellFound: 'ending.bell-found',
} as const;

export const ENDING_IDS = {
  bell: 'ending-bell',
} as const;

/** The plan's backstops. The truck is on a clock too, so the first never fires in practice. */
export const ENDING_HILL_PATIENCE = 40;
export const ENDING_FIND_PATIENCE = 90;

/** The shots, in seconds. */
export const ENDING_SECONDS = {
  /** The backstop walk: the camera finds it, she goes to it. */
  find: 4,
  mouth: 4,
  turn: 6,
  /**
   * The black holds while the last stroke rings out to silence, and then the
   * credits. The stroke's own tail is 4.5 s (`holly-bell-last`); the extra is
   * the silence after it, which is part of the sound.
   */
  black: 6,
} as const;

function clocked(id: string, seconds: number, cue: string): Stage {
  return { id, enter: (_ctx, emit): void => emit(cue), done: (ctx): boolean => ctx.stageTime >= seconds };
}

export const ENDING_BEAT: Beat = {
  number: 14,
  id: 'act3.ending',
  stages: [
    {
      id: 'the-hill',
      enter: (_ctx, emit): void => { emit(ENDING_CUES.open); emit(ENDING_CUES.hill); },
      done: (ctx): boolean => ctx.completed.has(ENDING_FLAGS.truckGone) || ctx.stageTime >= ENDING_HILL_PATIENCE,
    },
    {
      id: 'the-grass',
      enter: (_ctx, emit): void => emit(ENDING_CUES.grass),
      done: (ctx): boolean => ctx.completed.has(ENDING_FLAGS.bellFound) || ctx.stageTime >= ENDING_FIND_PATIENCE,
    },
    {
      // Zero seconds when she found it herself: `enter` sees the flag and the
      // stage is done on the same tick, so the mouth shot follows at once.
      id: 'the-find',
      enter: (ctx, emit): void => {
        if (!ctx.completed.has(ENDING_FLAGS.bellFound)) emit(ENDING_CUES.find);
      },
      done: (ctx): boolean => ctx.completed.has(ENDING_FLAGS.bellFound) || ctx.stageTime >= ENDING_SECONDS.find,
    },
    clocked('the-mouth', ENDING_SECONDS.mouth, ENDING_CUES.mouth),
    clocked('the-turn', ENDING_SECONDS.turn, ENDING_CUES.turn),
    clocked('black', ENDING_SECONDS.black, ENDING_CUES.black),
  ],
};
